import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { indiaData } from "../../data/indianStates";
import { motion, AnimatePresence } from "framer-motion";

interface DonationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type DonationForm = {
  name: string;
  email: string;
  phone: string;
  pan: string;
  state: string;
  district: string;
  addressLine: string;
  campaign_id: string;
};

const API_BASE = "https://www.sevaafoundation.com/seva-backend/api";

const DonationModal: React.FC<DonationModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  const [selectedAmount, setSelectedAmount] = useState<number | null>(1000);
  const [customAmount, setCustomAmount] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [campaigns, setCampaigns] = useState<any[]>([]);

  const [formData, setFormData] = useState<DonationForm>({
    name: "",
    email: "",
    phone: "",
    pan: "",
    state: "",
    district: "",
    addressLine: "",
    campaign_id: "",
  });

  const predefinedAmounts = [500, 1000, 2000, 5000];

  useEffect(() => {
    const handleOpenWithId = (event: Event) => {
      const customEvent = event as CustomEvent<{ campaignId?: number | string }>;
      if (customEvent.detail?.campaignId) {
        setFormData((previous) => ({
          ...previous,
          campaign_id: customEvent.detail.campaignId?.toString() || "",
        }));
      }
    };

    window.addEventListener("openDonationModal", handleOpenWithId);
    return () => {
      window.removeEventListener("openDonationModal", handleOpenWithId);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    // Fetch campaigns
    fetch(`${API_BASE}/campaigns/list.php`)
      .then((response) => response.json())
      .then((data) => {
        if (data.success && Array.isArray(data.campaigns)) {
          setCampaigns(data.campaigns);
        }
      })
      .catch((error) => console.error("Failed to fetch campaigns:", error));

    // Optional: Pre-fill data if user is logged in, but keep it fully editable
    const storedUser = localStorage.getItem("sevaa_user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setFormData((previous) => ({
          ...previous,
          email: parsedUser.email || "",
          name: parsedUser.name || "",
          phone: parsedUser.phone || "",
          pan: parsedUser.pan || "",
          state: parsedUser.state || "",
          district: parsedUser.district || "",
          addressLine: parsedUser.address || "",
        }));
      } catch {
        localStorage.removeItem("sevaa_user");
      }
    } else {
      setFormData((previous) => ({
        ...previous,
        name: "",
        email: "",
        phone: "",
        pan: "",
        state: "",
        district: "",
        addressLine: "",
      }));
    }
  }, [isOpen]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: name === "pan" ? value.toUpperCase().slice(0, 10) : value,
      ...(name === "state" ? { district: "" } : {}),
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const finalAmount = selectedAmount ?? Number(customAmount);

    if (!finalAmount || finalAmount < 1) {
      alert("Please enter a valid amount.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/donations/create.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount: finalAmount,
          ...formData,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to initialise donation.");
      }

      onClose();

      navigate("/checkout", {
        state: {
          amount: Number(data.amount ?? finalAmount),
          amountPaise: Number(data.amountPaise),
          currency: data.currency || "INR",
          donorDetails: formData,
          donationId: Number(data.donationId),
          orderId: data.orderId,
          keyId: data.keyId,
        },
      });
    } catch (error) {
      console.error("Donation submission failed:", error);
      alert(error instanceof Error ? error.message : "Failed to connect to the payment server.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const cannotSubmit = isSubmitting;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-[480px] overflow-hidden border border-slate-100 font-inter"
          >
            <div className="relative h-28 flex-shrink-0">
              <img
                src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?q=80&w=2070"
                className="w-full h-full object-cover"
                alt="Support SEVAA Foundation"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f766e] to-black/20" />
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="absolute top-2 right-2 text-white/80 hover:text-white p-1"
                aria-label="Close donation form"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M6 18L18 6M6 6l12 12" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
              <div className="absolute bottom-2 left-5">
                <span className="bg-orange-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded uppercase tracking-widest block w-fit mb-0.5">
                  Govt. Registered NGO
                </span>
                <h2 className="text-base font-bold text-white tracking-tight">Make a Donation ❤️</h2>
              </div>
            </div>

            <form onSubmit={handleSubmit} id="donation-form" className="p-5 space-y-3.5">
              <div>
                <div className="grid grid-cols-4 gap-2 mb-2">
                  {predefinedAmounts.map((amount) => (
                    <button
                      key={amount}
                      type="button"
                      onClick={() => {
                        setSelectedAmount(amount);
                        setCustomAmount("");
                      }}
                      className={`py-1.5 rounded-lg text-xs font-bold transition-all border-2 ${
                        selectedAmount === amount
                          ? "bg-[#0f766e] text-white border-[#0f766e]"
                          : "bg-slate-50 text-slate-600 border-transparent hover:border-slate-200"
                      }`}
                    >
                      ₹{amount}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  min="1"
                  step="1"
                  placeholder="Other Amount"
                  value={customAmount}
                  onChange={(event) => {
                    setCustomAmount(event.target.value);
                    setSelectedAmount(null);
                  }}
                  className="w-full p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold outline-none focus:border-[#0f766e]"
                />
              </div>

              <select
                name="campaign_id"
                value={formData.campaign_id}
                onChange={handleInputChange}
                className="w-full p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 outline-none focus:border-[#0f766e] transition-colors cursor-pointer"
              >
                <option value="">General Donation (Where it's needed most)</option>
                {campaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    Support: {campaign.title}
                  </option>
                ))}
              </select>

              <div className="grid grid-cols-2 gap-2.5">
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium outline-none focus:border-[#0f766e] focus:bg-white transition-all"
                />

                <input
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium outline-none focus:border-[#0f766e] transition-all"
                />

                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  required
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium outline-none focus:border-[#0f766e] transition-all"
                />

                <input
                  type="text"
                  name="pan"
                  placeholder="PAN Number (Optional)"
                  value={formData.pan}
                  onChange={handleInputChange}
                  className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium outline-none focus:border-[#0f766e] uppercase transition-all"
                />
              </div>

              {/* State and District are no longer required */}
              <div className="grid grid-cols-2 gap-2.5">
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium outline-none focus:border-[#0f766e] transition-all"
                >
                  <option value="">Select State (Optional)</option>
                  {Object.keys(indiaData).map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>

                <select
                  name="district"
                  value={formData.district}
                  onChange={handleInputChange}
                  disabled={!formData.state}
                  className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium outline-none focus:border-[#0f766e] disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100 transition-all"
                >
                  <option value="">Select District (Optional)</option>
                  {formData.state &&
                    indiaData[formData.state].map((district) => (
                      <option key={district} value={district}>
                        {district}
                      </option>
                    ))}
                </select>
              </div>

              {/* AddressLine is no longer required */}
              <input
                type="text"
                name="addressLine"
                placeholder="Full Address (Optional)"
                value={formData.addressLine}
                onChange={handleInputChange}
                className="w-full p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium outline-none focus:border-[#0f766e] transition-all"
              />

              <button
                type="submit"
                disabled={cannotSubmit}
                className="w-full bg-[#ffb000] hover:bg-[#e6a000] text-slate-900 font-black py-3 rounded-xl transition-all shadow-md text-xs uppercase tracking-widest disabled:opacity-70 mt-1"
              >
                {isSubmitting ? "Creating Secure Order..." : "Proceed to Secure Payment →"}
              </button>

              <p className="text-[8px] text-center text-slate-400 font-bold uppercase mt-[-4px]">
                Secure payment powered by Razorpay
              </p>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default DonationModal;